'use client'

import TemplateCard from '@/app/templates/TemplateCard'
import type { TemplateCategory } from '@/data/templates/templatesList'
import { templatesList } from '@/data/templates/templatesList'
import { cn } from '@/lib/utils'
import { AnimatePresence, motion } from 'framer-motion'
import { useEffect, useMemo, useState } from 'react'
import type { AppTemplateCategory } from 'sprites-types/ui/templates'
import AppSearchToolbar, { SortMode, SortOrder } from './AppSearchToolbar'
import NoResultsFound from './NoResultsFound'
import ShowMoreButton from './ShowMoreButton'

// Same categories as /templates page: Google Ads, Meta Ads, SEO only
const DASHBOARD_TEMPLATE_CATEGORIES: AppTemplateCategory[] = [
  { name: 'All', description: 'All templates', id: 'All' },
  { name: 'Google Ads', description: 'Google Ads templates', id: 'google-ads' },
  { name: 'Meta Ads', description: 'Meta Ads templates', id: 'meta-ads' },
  { name: 'SEO', description: 'SEO templates', id: 'seo' },
]

type SessionTemplatesProps = {
  className?: string
  headerText?: string
}
export default function SessionTemplates({
  className = '',
  headerText = 'Templates',
}: SessionTemplatesProps) {
  const [searchValue, setSearchValue] = useState<string>('')
  const [activeCategory, setActiveCategory] = useState<string>('All')

  const availableSortModes: SortMode[] = useMemo(() => ['Default', 'Alphabetical'], [])
  const [activeSortMode, setActiveSortMode] = useState<SortMode>('Default')
  const [activeSortOrder, setActiveSortOrder] = useState<SortOrder | null>(null)
  const [showAll, setShowAll] = useState(false)

  useEffect(() => {
    if (activeSortMode !== 'Default' && activeSortOrder === null) {
      setActiveSortOrder('Ascending')
    } else if (activeSortMode === 'Default') {
      setActiveSortOrder(null)
    }
  }, [activeSortMode, activeSortOrder])

  const handleCategoryChange = (categoryId: string | undefined) => {
    if (categoryId !== undefined) {
      setActiveCategory(categoryId)
    } else {
      setActiveCategory('All')
    }
  }

  const displayedCategories = useMemo(() => DASHBOARD_TEMPLATE_CATEGORIES, [])

  const filteredTemplates = useMemo(() => {
    return templatesList.filter(
      (t) =>
        (t.title?.toLowerCase().includes(searchValue.toLowerCase()) ||
          t.description?.toLowerCase().includes(searchValue.toLowerCase()) ||
          t.id?.toLowerCase().includes(searchValue.toLowerCase())) &&
        (activeCategory === 'All' || t.category === (activeCategory as TemplateCategory))
    )
  }, [searchValue, activeCategory])

  const renderFilteredSessionTemplates = useMemo(() => {
    // Apply sorting
    const sortedTemplates = [...filteredTemplates]
    if (activeSortMode === 'Alphabetical' && activeSortOrder) {
      sortedTemplates.sort((a, b) => {
        const nameA = (a.title || 'Untitled').toLowerCase()
        const nameB = (b.title || 'Untitled').toLowerCase()
        const compareResult = nameA.localeCompare(nameB)
        return activeSortOrder === 'Ascending' ? compareResult : -compareResult
      })
    }

    if (sortedTemplates.length <= 0) {
      return (
        <NoResultsFound
          message="No templates match your search"
          imageWidth={350}
          imageHeight={350}
        />
      )
    }

    const displayTemplates = showAll ? sortedTemplates : sortedTemplates.slice(0, 9)

    return (
      <>
        <ul className="mx-auto grid max-w-7xl grid-cols-1 gap-3 overflow-hidden sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4">
          <AnimatePresence mode="popLayout">
            {displayTemplates.map((template) => (
              <motion.div
                key={template.id}
                initial={{ opacity: 0, y: 10, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -20, scale: 0.9 }}
                transition={{
                  duration: 0.3,
                  ease: 'easeInOut',
                  layout: { duration: 0.3, ease: 'easeInOut' },
                }}
                layout="position"
              >
                <TemplateCard
                  id={template.id}
                  title={template.title}
                  description={template.description}
                  logo={template.logo}
                  message={template.message}
                  agent={template.agent}
                  compact
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </ul>
        {sortedTemplates.length > 9 && (
          <ShowMoreButton showAll={showAll} onClick={() => setShowAll(!showAll)} />
        )}
      </>
    )
  }, [filteredTemplates, showAll, activeSortMode, activeSortOrder])

  return (
    <div className={cn('container mx-auto flex flex-col gap-y-4 px-3 py-16 md:px-10', className)}>
      <div className="flex flex-col gap-y-2">
        <p
          className="ml-2 text-xl text-gray-900 md:text-2xl"
          style={{ fontFamily: 'Space Grotesk, sans-serif', letterSpacing: '0.01em' }}
        >
          {headerText}
        </p>
      </div>

      <AppSearchToolbar
        activeCategory={activeCategory}
        categories={displayedCategories}
        searchValue={searchValue}
        onCategoryChange={handleCategoryChange}
        onSearchChange={setSearchValue}
        availableSortModes={availableSortModes}
        activeSortMode={activeSortMode}
        activeSortOrder={activeSortOrder}
        onSortModeChange={setActiveSortMode}
        onSortOrderChange={setActiveSortOrder}
      />

      {renderFilteredSessionTemplates}
    </div>
  )
}
